import { useEffect, useMemo, useState } from 'react';
import Project from '../../../../models/Project';
import { CriteriaType, DEFAULT_PAGINATION_INDICATOR, IPaginationIndicator } from '../../../../models/PaginationIndicator';
import { sortAlphabetically, sortDecimalNumbers, sortNested } from '../../../../services/sortsUtil';
import { addSortableColumn } from './Util';

const readPagination = (): IPaginationIndicator => {
  const defaults = { ...DEFAULT_PAGINATION_INDICATOR, sortCriteria: 'name' };
  try {
    const stored = JSON.parse(sessionStorage.getItem('PaginationIndicator') || 'null');
    if (!stored) return defaults;
    const validCriteria = ['code', 'loanNumber', 'name', 'totalDisbursementAmount', 'tcdiPmrValue'];
    return {
      ...defaults,
      ...stored,
      sortCriteria: validCriteria.includes(stored.sortCriteria) ? stored.sortCriteria : 'name',
      currentPage: Number.isInteger(stored.currentPage) && stored.currentPage > 0 ? stored.currentPage : 1,
    };
  } catch {
    return defaults;
  }
};

export const useCountryTableRows = (projects: Project[], searchKeys: string[]) => {
  const [pagination, setPagination] = useState(readPagination);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const search = searchTerm.trim().toLowerCase();
  const hasActiveSearch = search.length >= 3;

  const rows = useMemo(() => addSortableColumn(projects), [projects]);
  const dataFilter = useMemo(() => {
    if (!hasActiveSearch) return rows;
    return rows.filter(project => searchKeys.some(key => {
      const value = project[key];
      return value !== null && value !== undefined && String(value).toLowerCase().includes(search);
    }));
  }, [rows, searchKeys, search, hasActiveSearch]);

  const { sortCriteria, sortAsc, type, nested } = pagination;
  const sortedRows = useMemo(() => {
    const order = sortAsc ? 'asc' : 'desc';
    const comparator = type === CriteriaType.nested
      ? sortNested(sortCriteria, order, nested || '')
      : type === CriteriaType.decimalNumber
        ? sortDecimalNumbers(sortCriteria, order)
        : sortAlphabetically(sortCriteria, order);
    return [...dataFilter].sort(comparator);
  }, [dataFilter, sortCriteria, sortAsc, type, nested]);

  const pagesCount = Math.ceil(sortedRows.length / rowsPerPage);
  const currentPage = Math.min(pagination.currentPage, Math.max(1, pagesCount));
  const currentList = sortedRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  useEffect(() => {
    sessionStorage.setItem('PaginationIndicator', JSON.stringify({ ...pagination, currentPage }));
    if (currentPage !== pagination.currentPage) {
      setPagination(previous => ({ ...previous, currentPage }));
    }
  }, [pagination, currentPage]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value !== searchTerm) {
      setPagination(previous => ({ ...previous, currentPage: 1 }));
    }
  };

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
    setPagination(previous => ({ ...previous, currentPage: 1 }));
  };

  const handlePaginationChange = (_event, value: number) => {
    setPagination(previous => ({ ...previous, currentPage: value }));
  };

  const handleSort = (sortCriteria: string, type: CriteriaType, nested: string | null = null) => {
    setPagination(previous => ({
      sortCriteria,
      type,
      nested,
      sortAsc: previous.sortCriteria === sortCriteria ? !previous.sortAsc : true,
      currentPage: 1,
    }));
  };

  return {
    dataFilter,
    currentList,
    currentPage,
    pagesCount,
    rowsPerPage,
    searchTerm,
    hasActiveSearch,
    handleSearchChange,
    handleRowsPerPageChange,
    handlePaginationChange,
    handleSort,
  };
};
